import {
  ButtonGroupOptions,
  ControlledButtonGroup,
  ControlledInput,
  Icon,
  Stack,
  colors,
  InputFormContainer,
  Typography,
  Indent,
} from '@ui-components/neo.tax';
import { ReactComponent as CloudIcon } from '@ui-components/neo.tax/dist/icons/cloud.svg';
import { ReactComponent as ContractorIcon } from '@ui-components/neo.tax/dist/icons/contractors.svg';
import { ReactComponent as SupplyIcon } from '@ui-components/neo.tax/dist/icons/lab-tube-bottle.svg';
import { ReactComponent as OverheadIcon } from '@ui-components/neo.tax/dist/icons/overhead.svg';

import { RD_CATEGORY_SINGULAR_LABELS } from '@neo.tax/rd/constants';
import { AccountingExpense } from '@neo.tax/rd/hooks/useAccountingExpense';
import { RdCategory, ACCOUNTING_PROVIDER_NAMES, ProviderLogo, noopFormSubmit } from '@neo.tax/shared';

import { EditQreExpenseAllocations } from '../qre-allocation/EditQreExpenseAllocations';
import { StyledAlert } from './accounting-expense-general.styled';
import { useAccountingExpenseFormAnalyticIds } from './hooks/useAccountingExpenseFormAnalyticIds';
import { useAccountingExpenseGeneralForm } from './hooks/useAccountingExpenseGeneralForm';

const RD_CATEGORY_OPTIONS: ButtonGroupOptions<RdCategory>[] = [
  {
    value: RdCategory.Contractor,
    icon: <Icon size={5} node={<ContractorIcon />} />,
    tooltip: RD_CATEGORY_SINGULAR_LABELS[RdCategory.Contractor],
  },
  {
    value: RdCategory.Cloud,
    icon: <Icon size={5} node={<CloudIcon />} />,
    tooltip: RD_CATEGORY_SINGULAR_LABELS[RdCategory.Cloud],
  },
  {
    value: RdCategory.Supply,
    icon: <Icon size={5} node={<SupplyIcon />} />,
    tooltip: RD_CATEGORY_SINGULAR_LABELS[RdCategory.Supply],
  },
  {
    value: RdCategory.Overhead,
    icon: <Icon size={5} node={<OverheadIcon />} />,
    tooltip: RD_CATEGORY_SINGULAR_LABELS[RdCategory.Overhead],
  },
  { value: RdCategory.NonRd, label: RD_CATEGORY_SINGULAR_LABELS[RdCategory.NonRd] },
];

type AccountingExpenseGeneralFormProps = {
  accountingExpense: AccountingExpense;
};

export const AccountingExpenseGeneralForm = ({ accountingExpense }: AccountingExpenseGeneralFormProps): JSX.Element => {
  const { nameAnalyticId, rdCategoryAnalyticId } = useAccountingExpenseFormAnalyticIds();

  const { control, watch } = useAccountingExpenseGeneralForm({ accountingExpense });
  const rdCategory = watch('rdCategory');

  const accountingProvider = accountingExpense.accountingQre.accountingProvider;

  return (
    <Stack as="form" direction="column" paddingX={4} fullHeight onSubmit={noopFormSubmit}>
      <ControlledInput
        control={control}
        name="displayName"
        id={nameAnalyticId}
        label="Vendor Name"
        rules={{ required: 'Vendor Name is required' }}
      />
      {accountingProvider && (
        <InputFormContainer inputId="ticketingProviderUsersAnalyticId" label="Linked Accounting Account">
          <Stack backgroundColor={colors.gray[100]} alignItems="center" gap={2} paddingX={3} paddingY={2}>
            <ProviderLogo provider={accountingProvider} size={20} />
            <Typography fontSize="xs" fontWeight="medium" as="span">
              {ACCOUNTING_PROVIDER_NAMES[accountingProvider]}
            </Typography>
          </Stack>
        </InputFormContainer>
      )}
      <ControlledButtonGroup
        control={control}
        name="rdCategory"
        id={rdCategoryAnalyticId}
        label="R&D Category"
        options={RD_CATEGORY_OPTIONS}
        rules={{ required: 'R&D Category is required' }}
        optionsGrow
      />
      <Indent>
        {rdCategory === RdCategory.Overhead && (
          <StyledAlert severity="neutral" subtitle="Overhead only counts for §174" />
        )}
        <EditQreExpenseAllocations rdExpenseId={accountingExpense.accountingQre.id} />
      </Indent>
    </Stack>
  );
};
